import { forwardRef, type ChangeEvent } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

interface ActionBasicInfoFieldsProps {
  name: string
  description: string
  onNameChange: (next: string) => void
  onDescriptionChange: (next: string) => void
  /** Shows the "Name is required" copy and sets aria-invalid. */
  nameError: boolean
  /** Placeholder for the name field. Catalog page uses the full example;
   *  the inline panel can shorten it. */
  namePlaceholder?: string
  descriptionPlaceholder?: string
  /** When true the Name field grabs focus on mount — used by the
   *  create-new flow in the node-agent inspector. */
  autoFocusName?: boolean
}

/**
 * The Name + Description fields the catalog detail page and the node-agent
 * inline inspector share. One component so a change to copy / layout /
 * validation UI lands in both places.
 */
export const ActionBasicInfoFields = forwardRef<
  HTMLInputElement,
  ActionBasicInfoFieldsProps
>(function ActionBasicInfoFields(
  {
    name,
    description,
    onNameChange,
    onDescriptionChange,
    nameError,
    namePlaceholder = 'Transfer to leasing',
    descriptionPlaceholder = 'Add helpful information about this action',
    autoFocusName,
  },
  ref,
) {
  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    onNameChange(e.target.value)
  }
  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onDescriptionChange(e.target.value)
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-1">
        <Label
          htmlFor="action-name"
          data-error={nameError}
          className="data-[error=true]:text-destructive px-3"
        >
          Name
        </Label>
        <Input
          id="action-name"
          ref={ref}
          value={name}
          onChange={handleNameChange}
          placeholder={namePlaceholder}
          aria-invalid={nameError}
          aria-describedby={nameError ? 'action-name-error' : undefined}
          autoFocus={autoFocusName}
        />
        {nameError && (
          <p id="action-name-error" className="text-destructive text-xs ml-3">
            Name is required
          </p>
        )}
      </div>

      <div className="grid gap-1">
        <Label htmlFor="action-description" className="px-3">
          Description
        </Label>
        <Textarea
          id="action-description"
          value={description}
          onChange={handleDescriptionChange}
          placeholder={descriptionPlaceholder}
        />
      </div>
    </div>
  )
})
